import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { formatDeleteRowsConfirmDialogMessages } from 'components/ApiTable/helpers/formatDeleteRowsConfirmDialogMessages';
import type { ApiTableModel } from 'components/ApiTable/model/ApiTableModel';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import { Button } from 'components/Button/Button';
import type {
  TableBulkAction,
  TableBulkActionClickEvent,
} from 'components/Table/model/TableModel';
import { useLocale } from 'containers/App/LocaleContext';
import type { ConfirmDialogMessages } from 'containers/ConfirmDialog/ConfirmDialog';
import type { SimpleConfirmDialogHook } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { type ReactElement, useCallback, useMemo } from 'react';

type Props = {
  confirmDialog: SimpleConfirmDialogHook;
  confirmDialogMessages?: Partial<ConfirmDialogMessages>;
  /**
   * Provide api table model to do data reloads.
   * Passed as getter to avoid cyclic dependencies with table initialization.
   */
  getApiTableModel: () => ApiTableModel;
  renderDeleteButton?: (
    id: string,
    handleDeleteClick: (id: string) => Promise<void>,
  ) => ReactElement;
} & (
  | {
      deleteOne: (id: string) => Promise<void>;
      deleteMultiple?: never;
    }
  | {
      deleteMultiple: (ids: string[]) => Promise<void>;
      deleteOne?: never;
    }
);

type ApiTableDeletionHelperHook = {
  deleteBulkAction: TableBulkAction;
  loading: boolean;
  renderDeleteButton: (id: string) => ReactElement;
};

/**
 * Contains common non-trivial logic for both single and bulk row deletion in a table.
 *
 * @example
 * // 1. Initialize the helper
 * const deletionHelper = useTableDeletionHelper({
 *   // provide shared confirm dialog, parent component ensures it is rendered
 *   confirmDialog,
 *   // optionally override part of or all confirm dialog messages
 *   confirmDialogMessages: jumphostMsg.deleteJumphostConfirmDialog,
 *   // provide access api table to reload data etc
 *   getApiTableModel: () => jumphostsApiTablePage.apiTableModel,
 *   // Provide endpoint to delete data. Ideally multiple with one query, but this component
 *   // can also work with endpoint deleting just one entry, calling it in a loop.
 *   deleteOne: (id: string) => api.request({...}),
 *   // OR
 *   deleteMultiple: (ids: string[]) => api.request({...}),
 *   // Optionally provide custom delete button renderer
 *   renderDeleteButton: (id: string, handleDeleteClick: (id: string) => Promise<void>) => (
 *     <CustomButton onClick={() => handleDeleteClick(id)} />
 *   ),
 * });
 *
 * // 2. Add bulk delete to bulk actions
 * const bulkActions = useMemo(
 *   (): TableBulkAction[] => [
 *     deletionHelper.deleteBulkAction,
 *   ],
 *   [],
 * );
 *
 * // 3. Render delete button in actions column
 * createFrontendOnlyColumn({
 *   key: 'action',
 *   disabledSort: true,
 *   title: jumphostMsg.columns.action,
 *   webDependencies: ['id'],
 *   render: (rowData: RowData) => (
 *     <div>
 *       {deletionHelper.renderDeleteButton(rowData.id)}
 *     </div>
 *   ),
 * }),
 *
 * // 4. Initialize api table
 * const jumphostsApiTablePage = useApiTablePage({
 *   endpoint,
 *   additionalApiTableProps: {
 *     ...,
 *     // apply bulk actions
 *     bulkActions,
 *     // propagate loading state to api table when a single item is being deleted
 *     loading: deletionHelper.loading,
 *   },
 * });
 */
export const useApiTableDeletionHelper = (
  props: Props,
): ApiTableDeletionHelperHook => {
  const deletionMsg = useLocale().generic.tableDeletionHelper;
  const [deleting, setDeleting] = useLoadingState();

  const deleteItems = useMemo(
    () =>
      props.deleteMultiple ??
      ((ids: string[]): Promise<unknown> =>
        Promise.all(ids.map(props.deleteOne))),
    [props.deleteOne, props.deleteMultiple],
  );

  const getDeleteRowsMsg = useCallback(
    (count: number) =>
      formatDeleteRowsConfirmDialogMessages(
        { ...deletionMsg.deleteConfirmDialog, ...props.confirmDialogMessages },
        count,
      ),
    [deletionMsg, props.confirmDialogMessages],
  );

  const handleDeleteClick = async (id: string) => {
    if (await props.confirmDialog.show(getDeleteRowsMsg(1))) {
      await withLoadingIndicator(setDeleting)(() => deleteItems([id]));
      props.getApiTableModel().dispatch(reloadTableData);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.confirmDialog.show): considered stable
  // biome-ignore lint/correctness/useExhaustiveDependencies(props.getApiTableModel): considered stable getter
  const deleteBulkAction: TableBulkAction = useMemo(
    () => ({
      key: 'delete',
      title: 'Delete',
      accent: 'negative',
      disableAutoDeselect: true,
      icon: 'fa-trash-can',
      onClick: async ({
        selectedRows,
        deselectAll,
        indicateRunningAction,
      }: TableBulkActionClickEvent) => {
        if (
          await props.confirmDialog.show(getDeleteRowsMsg(selectedRows.size))
        ) {
          await indicateRunningAction(async () => {
            await deleteItems([...(selectedRows as Set<string>)]);
          });
          deselectAll();
          props.getApiTableModel().dispatch(reloadTableData);
        }
      },
    }),
    [deleteItems, getDeleteRowsMsg],
  );

  const renderDeleteButton = useStableCallback((id: string) =>
    props.renderDeleteButton ? (
      props.renderDeleteButton(id, handleDeleteClick)
    ) : (
      <Button
        variant='tertiary'
        icon='fa-trash-can'
        onClick={() => handleDeleteClick(id)}
        size='xxs'
        type='button'
        dataTestId='api-table-delete-button'
      />
    ),
  );

  return useMemo(
    () => ({
      deleteBulkAction,
      loading: deleting === 'LOADING',
      renderDeleteButton,
    }),
    [deleting, deleteBulkAction],
  );
};
